import '../window-logger.js'

import { startTransition } from 'react'

import { notifyManager } from '@tanstack/react-query'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { createMemoryRouter, matchRoutes } from 'react-router'
import { createBrowserRouter } from 'react-router-dom'

import { getDomainInfo } from '@tribeplatform/react-components/common/utils'

import { createRoutes } from '../../routes.js'
import { InjectedAppProps } from '../../server/types/server.types.js'
import { APP_ROOT_ELEMENT_ID } from '../constants/app.constants.js'
import { polyfillJsFeatures } from '../lib/js-polyfills.js'
import { intlPolyfill } from '../utils/intlPolyfill.js'
import { localizeLibsForClient } from '../utils/localizeLibs.js'
import { getIntlLocale } from '../utils/translation/I18nStore.js'

import { ClientApp } from './components/ClientApp.js'
import { getInjectedAppProps, getTranslationDate } from './utils/entry.utils.js'

// Feature flags are resolved server-side during SSR (inside the per-request
// PostHog evaluation context) and serialized into the page via `__BM_DATA__`
// (`InjectedAppProps.flags`). The browser consumes that immutable snapshot
// through `<FlagsProvider>` in `ClientApp` and never evaluates flags itself,
// honoring the no-member-tracking contract.

/**
 * Route every react-query state notification through `startTransition` so
 * mount-time fetches commit as low-priority updates. Keeps their `setData`
 * out of React 18's initial hydration window — without this, any query
 * that resolves before hydration finishes triggers the recoverable
 * "Suspense boundary received an update before it finished hydrating"
 * warning and silently bails the affected boundary to CSR.
 *
 * Scoped to the web app (this entry file) rather than `react-sdk/Provider`
 * so other consumers (`dev-portal`, storybook, etc.) don't inherit a
 * notify wrap they don't need. Note: React 18 still bails Suspense
 * boundaries that receive any update *during* hydration regardless of
 * priority, so this is defense-in-depth for mid/late hydration cases.
 *
 * Trade-off (post-hydration): every react-query notification — including
 * user-triggered invalidations, unread counters, moderation counts — is
 * now flagged as a transition. In practice `startTransition` only defers
 * when React has higher-priority work in flight; user-event-driven
 * updates retain event priority and process effectively immediately, so
 * the post-hydration impact is minimal. If a specific consumer needs
 * synchronous `setData → re-render` ordering it can flush with `act()`
 * or temporarily override `notifyManager.setNotifyFunction` locally.
 *
 * See: https://tkdodo.eu/blog/react-query-and-react-18
 */
notifyManager.setNotifyFunction(callback => {
  startTransition(callback)
})

async function createRouter({
  isEmbed,
  injectedAppProps,
}: {
  injectedAppProps: InjectedAppProps
  isEmbed: boolean
}) {
  const {
    network: { domain, domainSubfolder },
  } = injectedAppProps
  const { basename } = getDomainInfo({
    domain,
    domainSubfolder,
  })

  const routes = createRoutes({ injectedAppProps })

  const lazyMatches = matchRoutes(routes, window.location, basename)?.filter(
    m => m.route.lazy,
  )

  // Load the lazy matches and update the routes before creating the router
  // so we can hydrate the SSR-rendered content synchronously
  if (lazyMatches && lazyMatches?.length > 0) {
    await Promise.all(
      lazyMatches.map(async m => {
        const routeModule = await m.route.lazy()
        Object.assign(m.route, {
          ...routeModule,
          lazy: undefined,
        })
      }),
    )
  }

  const router = isEmbed
    ? createMemoryRouter(routes, {
        basename,
        initialEntries: [
          `${window.location.pathname}${window.location.search}`,
        ],
      })
    : createBrowserRouter(routes, { basename })

  return router
}

const render = async () => {
  await polyfillJsFeatures()

  const container = document.getElementById(APP_ROOT_ELEMENT_ID)
  /**
   * The `isPWA` and `isEmbed` variables are added
   * to the window via a server-injected head script.
   * See server.entry.tsx for more details
   */
  // eslint-disable-next-line dot-notation
  const isPWA = window['isPWA'] === true
  // eslint-disable-next-line dot-notation
  const isEmbed = window['isEmbed'] === true

  const { lang } = document.documentElement

  const injectedAppProps = getInjectedAppProps()

  if (!injectedAppProps) {
    throw new Error('Missing initial app data')
  }

  const networkDomain = injectedAppProps?.network?.domain
  const networkDomainSubfolder = injectedAppProps?.network?.domainSubfolder

  if (!networkDomain) {
    throw new Error('Missing initial app data')
  }

  const { subfolder } = getDomainInfo({
    domain: networkDomain,
    domainSubfolder: networkDomainSubfolder,
  })

  // The Intl polyfills must be in place before `I18nStore` creates the intl:
  // `createIntl` captures `Intl.RelativeTimeFormat` / `Intl.ListFormat` by
  // reference and the eager `RESOURCE.*` aliases memoize `Intl.PluralRules` in
  // the shared cache, so CLDR data loaded afterwards is never consulted (and a
  // browser without a native `PluralRules` throws before the polyfill runs).
  // The tag is the one `I18nStore` hands to `createIntl` (`pt` → `pt-PT`); the
  // catalog fetch and dayjs/flatpickr stay on `lang`.
  await intlPolyfill(getIntlLocale(lang))

  const i18n = await getTranslationDate(lang, subfolder)

  if (!i18n) {
    throw new Error('Missing translation data')
  }

  await localizeLibsForClient(lang)

  const router = await createRouter({ injectedAppProps, isEmbed })

  if (isPWA) {
    const root = createRoot(container)
    root.render(
      <ClientApp
        i18n={i18n}
        injectedAppProps={injectedAppProps}
        router={router}
      />,
    )
  } else {
    // Load all components needed before rendering
    hydrateRoot(
      container,
      <ClientApp
        i18n={i18n}
        router={router}
        injectedAppProps={injectedAppProps}
      />,
      {
        // The type for onRecoverableError is wrong and doesn't include the errInfo param
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onRecoverableError: (error: unknown, errInfo: unknown) => {
          logger.error('Received a hydration error', {
            error,
            errInfo,
          })
        },
      },
    )
  }
}

render()
